import { ChangeEvent, FormEvent, useRef, useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Minus, Plus } from "lucide-react"
import { NewItem } from "@/types/purchase"
import { useAuth } from "@/hooks/useAuth"
const PRICE_ADJUSTMENT_UNIT = 1000;
const MINIMUM_QUANTITY = 1;
const INVALID_QUANTITY_ZERO_STRING = "0";

const INITIAL_NEW_ITEM: NewItem = { name: "", price: "0", quantity: "1" };

interface AddProductFormProps {
  onAddItem: (item: NewItem) => Promise<boolean>;
  hasItems?: boolean;
}

export function AddProductForm({
  onAddItem,
  hasItems = false,
}: AddProductFormProps) {
  const [newItem, setNewItem] = useState<NewItem>(INITIAL_NEW_ITEM);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();

  useEffect(() => {
    nameInputRef.current?.focus();
  }, []);

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setNewItem({ ...newItem, name: e.target.value });
  };

  const handlePriceChange = (e: ChangeEvent<HTMLInputElement>) => {
    // 숫자와 마이너스 기호만 허용
    const value = e.target.value.replace(/[^0-9-]/g, "");
    setNewItem({ ...newItem, price: value });
  };

  const handleQuantityChange = (e: ChangeEvent<HTMLInputElement>) => {
    // 숫자만 허용
    let value = e.target.value.replace(/[^0-9]/g, "");
    // 0은 1로 변경
    if (value === INVALID_QUANTITY_ZERO_STRING) value = String(MINIMUM_QUANTITY);
    setNewItem({ ...newItem, quantity: value });
  };

  // 가격 증가 버튼 클릭 처리
  const handlePriceIncrement = () => {
    const currentPrice = Number(newItem.price) || 0;
    setNewItem({ ...newItem, price: String(currentPrice + PRICE_ADJUSTMENT_UNIT) });
  };

  // 가격 감소 버튼 클릭 처리
  const handlePriceDecrement = () => {
    const currentPrice = Number(newItem.price) || 0;
    setNewItem({ ...newItem, price: String(Math.max(0, currentPrice - PRICE_ADJUSTMENT_UNIT)) });
  };

  // 수량 증가 버튼 클릭 처리
  const handleQuantityIncrement = () => {
    const newQuantity = Math.max(MINIMUM_QUANTITY, Number(newItem.quantity) + 1);
    setNewItem({ ...newItem, quantity: String(newQuantity) });
  };

  // 수량 감소 버튼 클릭 처리
  const handleQuantityDecrement = () => {
    const newQuantity = Math.max(MINIMUM_QUANTITY, Number(newItem.quantity) - 1);
    setNewItem({ ...newItem, quantity: String(newQuantity) });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!newItem.name.trim()) {
      alert("상품명을 입력해주세요.");
      return;
    }
    const success = await onAddItem(newItem);
    if (success) {
      setNewItem(INITIAL_NEW_ITEM);
      nameInputRef.current?.focus();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 p-4 border rounded-lg bg-gray-50/50" data-tour="product-list">
      {(user || hasItems) && (
        <h3 className="text-sm font-medium text-gray-600 -mb-2">상품 직접 추가</h3>
      )}
      {/* 상품명 입력 필드 */}
      <div className="grid sm:grid-cols-[auto_1fr] items-center gap-x-3 gap-y-2">
        <label htmlFor="productName" className="text-sm font-medium text-gray-700 shrink-0">상품명</label>
        <Input
          id="productName"
          ref={nameInputRef}
          placeholder="예: 와이드 팬츠 블랙"
          autoComplete="off"
          value={newItem.name}
          onChange={handleNameChange}
          className="w-full text-sm px-3 py-2.5 border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 text-center sm:text-left"
        />
      </div>

      {/* 가격 입력 필드 */}
      <div className="grid sm:grid-cols-[auto_1fr] items-center gap-x-3 gap-y-2">
        <label htmlFor="productPrice" className="text-sm font-medium text-gray-700 shrink-0">가격</label>
        <div className="flex items-center gap-2 w-full">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-10 w-10 border-gray-300 text-gray-600 hover:bg-gray-50 shrink-0"
            onClick={handlePriceDecrement}
            aria-label="가격 감소"
          >
            <Minus className="h-4 w-4" />
          </Button>
          <Input
            id="productPrice"
            type="text"
            inputMode="numeric"
            placeholder="0"
            autoComplete="off"
            value={newItem.price}
            onChange={handlePriceChange}
            className="flex-grow w-full sm:w-auto text-sm text-center px-3 py-2.5 border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-10 w-10 border-gray-300 text-gray-600 hover:bg-gray-50 shrink-0"
            onClick={handlePriceIncrement}
            aria-label="가격 증가"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* 수량 입력 필드 */}
      <div className="grid sm:grid-cols-[auto_1fr] items-center gap-x-3 gap-y-2">
        <label htmlFor="productQuantity" className="text-sm font-medium text-gray-700 shrink-0">수량</label>
        <div className="flex items-center gap-2 w-full">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-10 w-10 border-gray-300 text-gray-600 hover:bg-gray-50 shrink-0"
            onClick={handleQuantityDecrement}
            aria-label="수량 감소"
          >
            <Minus className="h-4 w-4" />
          </Button>
          <Input
            id="productQuantity"
            type="text"
            inputMode="numeric"
            value={newItem.quantity}
            onChange={handleQuantityChange}
            className="flex-grow w-full sm:w-auto text-sm text-center px-3 py-2.5 border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-10 w-10 border-gray-300 text-gray-600 hover:bg-gray-50 shrink-0"
            onClick={handleQuantityIncrement}
            aria-label="수량 증가"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <Button type="submit" className="w-full bg-slate-800 hover:bg-slate-900 text-white font-medium rounded-md transition-colors duration-150 ease-in-out py-2">
        상품 목록에 추가
      </Button>
    </form>
  );
}
